/**
 * @logue/vue2-helpers
 *
 * @description A util package to use Vue 2 with Composition API easily
 * @version 2.1.6
 * @license Apache-2.0
 * @see {@link https://github.com/logue/vue2-helpers#readme}
 *
 * Build: 2023-04-20T10:43:35.985Z
 */
import { isVue2, h as h$1, defineComponent, ref, watch, onMounted, onBeforeUnmount } from 'vue-demi';

/**
 * h-demi - h function for Vue 2 and 3
 *
 * @see {@link https://github.com/vueuse/vue-demi/issues/65}
 */
const adaptOnsV3 = (ons) => {
    if (!ons)
        return {};
    return Object.entries(ons).reduce((ret, [key, handler]) => {
        key = key.charAt(0).toUpperCase() + key.slice(1);
        key = `on${key}`;
        return { ...ret, [key]: handler };
    }, {});
};
/**
 * hDemi function.
 */
function h(type, options, chidren) {
    if (isVue2) {
        return h$1(type, options, chidren);
    }
    const ons = options.on != null ? adaptOnsV3(options.on) : {};
    return h$1(type, { ...options.props, ...options.domProps, ...ons }, chidren);
}

/**
 * Teleport Component.
 *
 * Original version by Mechazawa's 'vue2-teleport.
 * Composition api version By Logue.
 */
const Teleport = defineComponent({
    /** Component Name */
    name: 'Teleport',
    /** Props Definition */
    props: {
        to: {
            type: String,
            required: true,
        },
        where: {
            type: String,
            default: 'after',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    /**
     * Setup
     *
     * @param props  - Props
     * @param _context - Context
     */
    setup(props, _context) {
        const teleport = ref();
        const nodes = ref([]);
        const waiting = ref(false);
        const observer = ref(null);
        const childObserver = ref(null);
        const parent = ref(null);
        watch(() => props.to, () => {
            maybeMove();
        });
        watch(() => props.where, () => {
            maybeMove();
        });
        watch(() => props.disabled, value => {
            if (value) {
                disable();
                teardownObserver();
            }
            else {
                bootObserver();
                move();
            }
        });
        onMounted(() => {
            if (teleport.value != null) {
                // Store a reference to the nodes
                nodes.value = Array.from(teleport.value.childNodes);
            }
            if (!props.disabled) {
                bootObserver();
            }
            // Move slot content to target
            maybeMove();
        });
        onBeforeUnmount(() => {
            disable();
            teardownObserver();
        });
        const maybeMove = () => {
            if (!props.disabled) {
                move();
            }
        };
        const move = () => {
            waiting.value = false;
            parent.value = document.querySelector(props.to);
            if (parent.value == null) {
                disable();
                waiting.value = true;
                return;
            }
            if (props.where === 'before') {
                parent.value.prepend(getFragment());
            }
            else {
                parent.value.appendChild(getFragment());
            }
        };
        const disable = () => {
            teleport.value?.appendChild(getFragment());
            parent.value = null;
        };
        const getFragment = () => {
            // Using a fragment is faster because it'll trigger only a single reflow
            // See https://developer.mozilla.org/en-US/docs/Web/API/DocumentFragment
            const fragment = document.createDocumentFragment();
            nodes.value.forEach(node => fragment.appendChild(node));
            return fragment;
        };
        const onMutations = (mutations) => {
            // Makes sure the move operation is only done once
            let shouldMove = false;
            mutations.forEach(mutation => {
                const filteredAddedNodes = Array.from(mutation.addedNodes).filter(node => !nodes.value.includes(node));
                if (parent.value != null &&
                    Array.from(mutation.removedNodes).includes(parent.value)) {
                    disable();
                    waiting.value = !props.disabled;
                }
                else if (waiting.value && filteredAddedNodes.length > 0) {
                    shouldMove = true;
                }
            });
            if (shouldMove) {
                move();
            }
        };
        const bootObserver = () => {
            if (observer.value == null) {
                observer.value = new MutationObserver((mutations) => {
                    onMutations(mutations);
                });
                observer.value.observe(document.body, {
                    childList: true,
                    subtree: true,
                    attributes: false,
                    characterData: false,
                });
            }
            if (childObserver.value == null) {
                // watch childNodes change
                childObserver.value = new MutationObserver((mutations) => {
                    const childChangeRecord = mutations.find(i => i.target === teleport.value);
                    if (childChangeRecord != null && teleport.value != null) {
                        nodes.value = Array.from(teleport.value.childNodes);
                        maybeMove();
                    }
                });
            }
        };
        const teardownObserver = () => {
            if (observer.value != null) {
                observer.value.disconnect();
                observer.value = null;
            }
            if (childObserver.value != null) {
                childObserver.value.disconnect();
                childObserver.value = null;
            }
        };
        return {
            teleport,
            nodes,
            waiting,
            observer,
            parent,
        };
    },
    render() {
        // <template>
        //   <div ref="teleport" class="vue-teleport">
        //     <slot />
        //   </div>
        // </template>
        return h('div', {
            ref: 'teleport',
            class: 'vue-teleport',
            style: !this.$props.disabled
                ? 'visibility: hidden; display: none;'
                : '',
        }, this.$slots.default);
    },
});
const install = (app) => app.component('Teleport', Teleport);
if (window.Vue) {
    // @ts-expect-error Register to window's Vue object
    window.Vue.use(Teleport);
}

export { Teleport, Teleport as default, install };
//# sourceMappingURL=teleport.es.js.map
